import { z } from 'zod'

import { type TeamUserRole, roleSchema } from '@/team'

// Access token payload will move to being user defined in the future
// Must always be a flat primitive record like Record<string, string|number|boolean>
export type AccessTokenPayload = {
  userIndex: string
  userUid: string
  teamUid: string
  email: string | null
  role?: TeamUserRole
  exp: number
}

export const accessTokenPayloadSchema = z.object({
  userIndex: z.string(),
  userUid: z.string(),
  teamUid: z.string(),
  email: z.string().nullable(),
  role: roleSchema.optional(),
  exp: z.number().int(),
}) satisfies z.ZodType<AccessTokenPayload>

export type RefreshTokenPayload = {
  /** Uid of token */
  uid: string
  /** Uid of team */
  teamUid: string | null
  /** Type of the token. Always set to 'refresh' for now */
  type: string
  /** Token family ID to associate a login sessions set of refresh tokens */
  familyId: string
  /** Unique user index to query user record */
  userIndex: string
  /** Expiry in seconds */
  exp: number
}
export const refreshTokenPayloadSchema: z.ZodType<RefreshTokenPayload> =
  z.object({
    uid: z.string(),
    teamUid: z.string().nullable(),
    type: z.string(),
    familyId: z.string(),
    userIndex: z.string(),
    exp: z.number(),
  })

export type PersonalTokenPayload = {
  /** Uid of token */
  uid: string
  /** Uid of team */
  teamUid: string
  /** Unique user index to query user record */
  userIndex: string
  /** Token generation timestamp in unix seconds */
  iat: number
  /** Expiry in seconds */
  exp: number
}

/**
 * Long lived personal token related to a user and a team.
 */
export const personalTokenPayloadSchema: z.ZodType<PersonalTokenPayload> =
  z.object({
    uid: z.string(),
    teamUid: z.string(),
    userIndex: z.string(),
    iat: z.number(),
    exp: z.number(),
  })

/**
 * Short lived exchange token to retrieve recently issued
 * access and refresh tokens from the database.
 */
export const exchangeTokenSchema = z.object({
  uid: z.string(),
  accessToken: z.string(),
  refreshToken: z.string(),
  expiry: z.number(),
  teamUid: z.string(),
})

export type ExchangeTokenPayload = z.infer<typeof exchangeTokenSchema>
